import {
  SelectField,
  type SelectFieldOption,
  type SelectFieldProps,
} from '@wandb/components';
import {clsx} from 'clsx';
import {type DocumentNode} from 'graphql';
import * as React from 'react';

import {useLazyQuery} from '../../state/graphql/apolloWithAbort';
import {useInfiniteItems, useInfinitePageDetails} from './InfiniteHooks';
import {type ExtractPageInfo} from './types';
import {useInfiniteResults} from './useInfiniteResults';

const PAGE_SIZE = 30;
const DEFAULT_EXTRA_QUERY_VARIABLES = {};

/**
 * Default mapping from the search query to the paginated query's search
 * variables. Matches the `runFieldValues` shape, which is the most common
 * consumer.
 */
const defaultBuildSearchVariables = (
  searchString: string
): Record<string, unknown> => ({
  pattern: searchString,
  valuePattern: searchString,
});

export const CUSTOM_VALUE_DESCRIPTION = 'Use this exact value';

/** `TData` is the query's generated result type. */
export type InfiniteSelectFieldConfig<TData = unknown> = {
  /** Accessible name of the field, e.g. "Value". */
  label: string;
  placeholder: string;
  searchPlaceholder: string;
  /** Option values from one page of results, as the list should show them. */
  extractItems: (data: TData | undefined) => string[];
  extractPageInfo: ExtractPageInfo<TData>;
  /** A paginated query taking `entityName`, `projectName`, `first`, `after`. */
  query: DocumentNode;
  /**
   * Maps the search query into the query's search variables. Defaults to
   * `{pattern, valuePattern}` for the runFieldValues query.
   */
  buildSearchVariables?: (searchString: string) => Record<string, unknown>;
};

export type InfiniteSelectFieldProps<TData = unknown> = {
  config: InfiniteSelectFieldConfig<TData>;
  entityName: string;
  projectName: string;
  extraQueryVariables?: any;
  /** Skip fetching, e.g. while the filter has no key yet. */
  skip?: boolean;
  isMultiSelect: boolean;
  isValid?: boolean;
  hookConfig?: {
    useQueryHook?: typeof useLazyQuery;
  };
  /** Stored selections, already stringified the way the list displays them. */
  selections: string[];
  /**
   * Receives the whole selection after any change: a pick, an unpick, a tag
   * removed from the trigger, or "clear all".
   */
  onSelectionsChange: (selections: string[]) => void;
  /** Extra classes for the wrapper that sizes the field within a filter row. */
  className?: string;
  'data-test'?: string;
};

// The label props are a one-of union that Omit would collapse, so each
// branch below passes the accessible name itself.
type SharedSelectFieldProps = Omit<
  SelectFieldProps<'single'>,
  | 'value'
  | 'defaultValue'
  | 'onChange'
  | 'selectionMode'
  | 'validate'
  | 'label'
  | 'aria-label'
>;

/**
 * A `SelectField` fed by a paginated GraphQL query: the first page loads on
 * mount, scrolling to the end loads the next page, and typing in the search
 * box refetches from the first page with the query's search variables.
 *
 * Two things the plain field would not do on its own:
 * - Stored selections are kept in the option list even when the loaded page
 *   or the current search does not contain them, so the trigger can always
 *   label the current value. Loaded options keep their server order.
 * - A query that matches no loaded option exactly is offered as an option of
 *   its own, so a value the server did not suggest (a threshold for a
 *   numeric comparison, say) can still be filtered on.
 */
export const InfiniteSelectField = <TData,>({
  config,
  entityName,
  projectName,
  extraQueryVariables = DEFAULT_EXTRA_QUERY_VARIABLES,
  skip = false,
  isMultiSelect,
  isValid = true,
  hookConfig,
  selections,
  onSelectionsChange,
  className,
  'data-test': dataTest,
}: InfiniteSelectFieldProps<TData>) => {
  const {addListItems, clearListItems, listItems} = useInfiniteItems();
  const {pageDetails, updatePageDetails} = useInfinitePageDetails();
  const [searchString, setSearchString] = React.useState('');

  const {fetchMore, isLoading, error} = useInfiniteResults(
    {
      entityName,
      projectName,
      addListItems,
      extractItems: config.extractItems,
      extractPageInfo: config.extractPageInfo,
      query: config.query,
      extraQueryVariables,
      updatePageDetails,
    },
    {
      useQueryHook: hookConfig?.useQueryHook ?? useLazyQuery,
    }
  );

  // `makeInfiniteSelectField` binds `config` once at module scope.
  const buildSearchVariables =
    config.buildSearchVariables ?? defaultBuildSearchVariables;

  // `fetchMore` is Apollo's execute function, which keeps its identity, so
  // this only changes when the query itself does: the search text, the
  // caller's variables, or `skip`.
  const fetchPage = React.useCallback(
    (after: string) => {
      if (skip) {
        return;
      }
      fetchMore({
        variables: {
          first: PAGE_SIZE,
          ...buildSearchVariables(searchString),
          after,
          ...extraQueryVariables,
        },
      });
    },
    [fetchMore, buildSearchVariables, searchString, extraQueryVariables, skip]
  );

  // Load the first page when the field mounts, as the downshift dropdown did,
  // and again whenever the query changes. `setQuery` has emptied the list by
  // then, so the effect refills it from the first page.
  React.useEffect(() => {
    fetchPage('');
  }, [fetchPage]);

  const setQuery = React.useCallback(
    (next: string) => {
      clearListItems();
      // hasNextPage stays false until the first page of the new query lands,
      // so the load-more sentinel cannot request page two of an empty list.
      updatePageDetails(false, '');
      setSearchString(next);
    },
    [clearListItems, updatePageDetails]
  );

  const options = React.useMemo<SelectFieldOption[]>(() => {
    const seen = new Set<string>();
    const out: SelectFieldOption[] = [];
    const add = (value: string, description?: string) => {
      if (seen.has(value)) {
        return;
      }
      seen.add(value);
      out.push(
        description == null
          ? {id: value, label: value}
          : {id: value, label: value, description}
      );
    };
    // Loaded options first, in server order, so picking one does not move it
    // (the popover stays open for multi-value filters); stored selections the
    // page does not contain follow, so the trigger can still label them.
    for (const item of listItems) {
      add(item);
    }
    for (const selection of selections) {
      add(selection);
    }
    const query = searchString.trim();
    if (query !== '' && !isLoading && !seen.has(query)) {
      add(query, CUSTOM_VALUE_DESCRIPTION);
    }
    return out;
  }, [selections, listItems, searchString, isLoading]);

  const shared: SharedSelectFieldProps = {
    'data-test': dataTest,
    placeholder: config.placeholder,
    items: options,
    isInvalid: !isValid || error != null,
    isLoading,
    hasMore: pageDetails.hasNextPage,
    onLoadMore: () => {
      if (!isLoading && pageDetails.hasNextPage) {
        fetchPage(pageDetails.endCursor);
      }
    },
    isVirtualized: true,
    fullWidth: true,
    search: {
      value: searchString,
      onInput: setQuery,
      placeholder: config.searchPlaceholder,
    },
    onOpenChange: isOpen => {
      if (!isOpen && searchString !== '') {
        setQuery('');
      }
    },
  } as SharedSelectFieldProps;

  return (
    <div className={clsx('min-w-0 flex-grow', className)}>
      {isMultiSelect ? (
        <SelectField
          {...shared}
          aria-label={config.label}
          selectionMode="multiple"
          value={selections}
          onChange={keys => onSelectionsChange(keys.map(String))}
        />
      ) : (
        <SelectField
          {...shared}
          aria-label={config.label}
          value={selections[0] ?? null}
          onChange={key => onSelectionsChange(key == null ? [] : [String(key)])}
        />
      )}
    </div>
  );
};

/**
 * Binds the field to one paginated query so consumers only supply the
 * project and the selection state.
 */
export const makeInfiniteSelectField =
  <TData,>(config: InfiniteSelectFieldConfig<TData>) =>
  (props: Omit<InfiniteSelectFieldProps<TData>, 'config'>) => (
    <InfiniteSelectField config={config} {...props} />
  );
